import { useIsMutating } from '@tanstack/react-query'
import {
  useCreateElplanBidsMutation,
  useDeleteElplanBidsForDateMutation,
  useElplanBidCreateMutation,
  useElplanBidDeleteMutation,
  useElplanBids,
  useElplanBidUpdateMutation,
  useUpdateElplanBidsMutation,
} from 'api/elplan/elplan.api'
import { resolveElplanBidsQueryEndTime } from 'api/elplan/elplan.bidQueryWindow.helper'
import { ELPLAN_BID_WRITE_MUTATION_KEY } from 'api/elplan/elplan.constants'
import { BidTypeName, ElplanInfoTagForDate, PlanValues } from 'api/elplan/elplan.types'
import { postStat } from 'api/stats/stats.api'
import { convertNumberDecimalSign, isArray } from 'helpers/global.helper/global.helper'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import { Button } from 'ui/atoms'
import Title from 'ui/molecules/Title/Title'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import {
  FormattedBid,
  FormattedBidWithIdAndTitle,
  formatBid,
  generatePlanValues,
  getDefaultBlockBid,
} from '../../DayAheadBidManagement/DayAheadBidManagement.helper'
import BlockBidTable from './BlockBidTable/BlockBidTable'
import styles from './DayAheadBidEditor.module.less'
import type { BidModalTagSelectionContext } from './DayAheadBidEditor.types'
import SpotBidTable from './SpotBidTable/SpotBidTable'
import { useCopySpotBidsFromPlan } from './useCopySpotBidsFromPlan'
import { useDayWideBidLimit } from './useDayWideBidLimit'

const DEFAULT_MAX_NUMBER_OF_BLOCK_BIDS = 100

type DayAheadBidEditorProps = {
  filteredBidTypeTypes?: BidTypeName[]
  scenarioId?: number
  showAllScenarioBids?: boolean
  partitionId?: number
  regularPlanSubtypeAliases?: UiConfigAliases
  datasetStartTime: ISODateTime
  datasetEndTime: ISODateTime
  disabledBidButtons?: boolean
  tagsForDate?: ElplanInfoTagForDate[]
  bidInputCurrencies?: {
    block: string
    profile: string
  }
  requireTagForBlockBids?: boolean
  setCurrentBlockBid: (bid: FormattedBidWithIdAndTitle) => void
  setCurrentProfileBid: (bid: FormattedBidWithIdAndTitle) => void
  setTypeOfBidToCopy: (bidType: BidTypeName, partitionId?: number) => void
}

export function DayAheadBidEditor({
  datasetStartTime,
  datasetEndTime,
  filteredBidTypeTypes,
  scenarioId,
  showAllScenarioBids,
  partitionId,
  regularPlanSubtypeAliases,
  disabledBidButtons,
  tagsForDate = [],
  bidInputCurrencies = { block: 'SEK', profile: 'SEK' },
  requireTagForBlockBids = false,
  setCurrentBlockBid,
  setCurrentProfileBid,
  setTypeOfBidToCopy,
}: DayAheadBidEditorProps) {
  const { t } = useTranslation()
  const elplanSnap = useSnapshot(elplanStore)
  const bidWritesPending = useIsMutating({ mutationKey: [ELPLAN_BID_WRITE_MUTATION_KEY] }) > 0
  const { copyingSpotBidsFromPlan, copySpotBidsFromPlan } = useCopySpotBidsFromPlan({
    datasetStartTime,
    datasetEndTime,
    regularPlanSubtypeAliases,
    scenarioId,
    partitionId,
    elplanInfoId: elplanSnap.elplan_info_id,
  })

  const { mutate: createBid } = useElplanBidCreateMutation()
  const { mutate: updateBid } = useElplanBidUpdateMutation()
  const { mutate: deleteBid } = useElplanBidDeleteMutation()

  const { mutateAsync: createSpotBidsForDate } = useCreateElplanBidsMutation({ suppressAlerts: true })
  const { mutateAsync: updateSpotBids } = useUpdateElplanBidsMutation({ suppressAlerts: true })
  const { mutateAsync: deleteSpotBidsFromDate } = useDeleteElplanBidsForDateMutation()

  const blockBidBidType = elplanSnap.bid_types.find((bid) => bid.type === 'blockbid')
  const profileBidBidType = elplanSnap.bid_types.find((bid) => bid.type === 'profilebid')
  const maxAmountOfBlockBids = blockBidBidType?.amount ?? DEFAULT_MAX_NUMBER_OF_BLOCK_BIDS
  const maxAmountOfProfileBids = profileBidBidType?.amount ?? DEFAULT_MAX_NUMBER_OF_BLOCK_BIDS

  const { hasSpotBids, hasBlockBids, hasProfileBids } = useMemo(() => {
    const noFilteredSpotBidTypes = !filteredBidTypeTypes || filteredBidTypeTypes.length === 0

    const systemHasSpotBids = elplanSnap.bid_types.some(
      (bid) => bid.type === 'spotbid_without_price' || bid.type === 'spotbid_with_price'
    )
    const scenarioHasSpotBids =
      noFilteredSpotBidTypes ||
      filteredBidTypeTypes?.some((type) => type === 'spotbid_without_price' || type === 'spotbid_with_price')
    const systemHasBlockBids = elplanSnap.bid_types.some((bidType) => bidType.type === 'blockbid')
    const scenarioHasBlockBids = noFilteredSpotBidTypes || filteredBidTypeTypes?.some((type) => type === 'blockbid')

    const systemHasProfileBids = elplanSnap.bid_types.some((bidType) => bidType.type === 'profilebid')
    const scenarioHasProfileBids = noFilteredSpotBidTypes || filteredBidTypeTypes?.some((type) => type === 'profilebid')

    return {
      hasSpotBids: systemHasSpotBids && scenarioHasSpotBids,
      hasBlockBids: systemHasBlockBids && scenarioHasBlockBids,
      hasProfileBids: systemHasProfileBids && scenarioHasProfileBids,
    }
  }, [elplanSnap.bid_types, filteredBidTypeTypes])
  const bidsQueryEndDate = resolveElplanBidsQueryEndTime(datasetEndTime)
  const bidsQuery = useElplanBids(
    elplanSnap.elplan_info_id,
    datasetStartTime,
    bidsQueryEndDate,
    showAllScenarioBids ? undefined : scenarioId,
    partitionId
  )
  const { data: topicalBids } = bidsQuery
  const bidListBusy = copyingSpotBidsFromPlan || bidWritesPending || bidsQuery.isFetching
  const { status: combinedBidLimitStatus, reachedLimit: hasReachedCombinedBlockAndProfileBidLimit } =
    useDayWideBidLimit({
      elplanInfoId: elplanSnap.elplan_info_id,
      startTime: datasetStartTime,
      endTime: bidsQueryEndDate,
      scenarioId,
      partitionId,
      topicalBids,
      maxBlockAndProfileBids: elplanSnap.max_amount_block_and_profile_bids,
    })

  const { spotBids, blockBids, profileBids } = useMemo(() => {
    if (!topicalBids)
      return {
        spotBids: [],
        blockBids: [],
        profileBids: [],
      }

    const formatted = topicalBids?.map((bid) => formatBid(bid))
    const spotBids = formatted?.filter((bid) => bid.type === 'spotbid_without_price')
    const blockBids = formatted?.filter((bid) => bid.type === 'blockbid')
    const profileBids = formatted?.filter((bid) => bid.type === 'profilebid')

    blockBids?.sort((a, b) => (a.block_order && b.block_order ? a.block_order - b.block_order : 0))
    profileBids?.sort((a, b) => (a.block_order && b.block_order ? a.block_order - b.block_order : 0))
    profileBids?.forEach((bid, index) => {
      bid.title = t('Profile bid') + (bid.block_order ? ` ${bid.block_order}` : ` ${index + 1}`)
    })

    return {
      spotBids,
      blockBids,
      profileBids,
    }
  }, [t, topicalBids])

  const disableForCombinedBidLimit =
    combinedBidLimitStatus === 'unresolved' || hasReachedCombinedBlockAndProfileBidLimit
  const combinedBidLimitExceededTooltip = t('Maximum total number of block and profile bids has been reached')
  const bidModalTagSelectionContext: BidModalTagSelectionContext = {
    tagsForDate,
    requireTagForBlockBids,
    bidInputCurrencies,
  }

  const openBlockBidModal = useCallback(() => {
    const emptyBid = getDefaultBlockBid({
      startTime: datasetStartTime,
      endTime: datasetEndTime,
    })
    setCurrentBlockBid({ ...emptyBid, scenarioId, partitionId })
  }, [datasetEndTime, datasetStartTime, partitionId, scenarioId, setCurrentBlockBid])

  const openProfileBidModal = useCallback(() => {
    const emptyBid = getDefaultBlockBid({
      startTime: datasetStartTime,
      endTime: datasetEndTime,
    })

    setCurrentProfileBid({
      ...emptyBid,
      type: 'profilebid',
      title: t('Profile bid'),
      scenarioId,
      partitionId,
      planValues: generatePlanValues(0, datasetStartTime, datasetEndTime),
    })
  }, [scenarioId, partitionId, datasetEndTime, datasetStartTime, setCurrentProfileBid, t])

  const addNewBid = useCallback(
    (bid: FormattedBid) => {
      createBid({
        elplan_info: elplanSnap.elplan_info_id,
        tag: bid.tag,
        start_time: Datetime.toISOString(bid.startTime),
        end_time: Datetime.toISOString(bid.endTime),
        type: bid.type,
        block_order: bid.block_order,
        volume: isArray(bid.volume)
          ? bid.volume.map((vol) => convertNumberDecimalSign(vol) as number)
          : convertNumberDecimalSign(bid.volume ?? 0),
        price: convertNumberDecimalSign(bid.price ?? 0),
        name: bid.name || '-',
        electricity_price_scenario: scenarioId,
        ...(partitionId === undefined ? {} : { partition: partitionId }),
      })
    },
    [createBid, elplanSnap.elplan_info_id, partitionId, scenarioId]
  )

  const removeBlockBid = useCallback(
    (bid: FormattedBidWithIdAndTitle) => {
      if (bid.id && !bid.fake) {
        deleteBid({ id: bid.id })
      }
    },
    [deleteBid]
  )

  const editBid = useCallback(
    (bid: FormattedBidWithIdAndTitle) => {
      updateBid({
        id: bid.id,
        tag: bid.tag,
        start_time: Datetime.toISOString(bid.startTime),
        end_time: Datetime.toISOString(bid.endTime),
        volume: isArray(bid.volume)
          ? bid.volume.map((vol) => convertNumberDecimalSign(vol) as number)
          : convertNumberDecimalSign(bid.volume ?? 0),
        price: convertNumberDecimalSign(bid.price ?? 0),
        name: bid.name,
      })
    },
    [updateBid]
  )

  const addNewSpotBidsForDate = useCallback(
    (spotBids: PlanValues[]) => {
      const bids = spotBids.map((bid) => {
        return { time: bid.start_time, volume: convertNumberDecimalSign(bid.volume) as number }
      })
      return createSpotBidsForDate({
        elplan_info: elplanSnap.elplan_info_id,
        type: 'spotbid_without_price',
        electricity_price_scenario: scenarioId,
        ...(partitionId === undefined ? {} : { partition: partitionId }),
        bids,
      })
    },
    [createSpotBidsForDate, elplanSnap.elplan_info_id, partitionId, scenarioId]
  )

  const editSpotBidsForDate = useCallback(
    (spotBids: PlanValues[]) => {
      const bidsToBeUpdated = spotBids
        .map((bid) => {
          return { id: bid.id, volume: convertNumberDecimalSign(bid.volume) }
        })
        .filter((bid) => bid.id)

      return updateSpotBids({
        elplan_info: elplanSnap.elplan_info_id,
        ...(partitionId === undefined ? {} : { partition: partitionId }),
        bids: bidsToBeUpdated,
      })
    },
    [elplanSnap.elplan_info_id, partitionId, updateSpotBids]
  )

  const removeSpotBidsForDate = useCallback(
    (date: ISODateTime) => {
      return deleteSpotBidsFromDate({
        elplan_info: elplanSnap.elplan_info_id,
        date: Datetime.toLocalTime(date, 'onlyDate') as ISODateTime,
        type: 'spotbid_without_price',
        ...(partitionId === undefined ? {} : { partition: partitionId }),
      })
    },
    [deleteSpotBidsFromDate, elplanSnap.elplan_info_id, partitionId]
  )

  return (
    <>
      {(hasBlockBids || hasProfileBids) && (
        <div className={styles.DayAheadBidEditor_Tables}>
          <Title size="medium" title={t('Block bid editor')} />
          <div className={styles.DayAheadBidEditor_Tables__right}>
            {hasBlockBids && (
              <Button
                primary
                icon="fal fa-plus"
                marginRight
                disabled={blockBids?.length >= maxAmountOfBlockBids || disableForCombinedBidLimit || disabledBidButtons}
                disabledTooltip={
                  hasReachedCombinedBlockAndProfileBidLimit ? combinedBidLimitExceededTooltip : undefined
                }
                onClick={openBlockBidModal}
              >
                {t('Create block bid')}
              </Button>
            )}
            {hasProfileBids && (
              <Button
                primary
                icon="fal fa-plus"
                marginRight
                disabled={
                  profileBids?.length >= maxAmountOfProfileBids || disableForCombinedBidLimit || disabledBidButtons
                }
                disabledTooltip={
                  hasReachedCombinedBlockAndProfileBidLimit ? combinedBidLimitExceededTooltip : undefined
                }
                onClick={openProfileBidModal}
              >
                {t('Create profile bid')}
              </Button>
            )}

            {hasBlockBids && (
              <Button
                onClick={() => {
                  setTypeOfBidToCopy('blockbid', partitionId)
                }}
                secondary
                marginRight
                disabled={(blockBids?.length ? true : false) || disabledBidButtons}
                icon={'fas fa-copy'}
              >
                {t('Copy block bids from a chosen date')}
              </Button>
            )}
            {hasProfileBids && (
              <Button
                onClick={() => {
                  setTypeOfBidToCopy('profilebid', partitionId)
                }}
                secondary
                disabled={(profileBids?.length ? true : false) || disabledBidButtons}
                icon={'fas fa-copy'}
              >
                {t('Copy profile bids from a chosen date')}
              </Button>
            )}
          </div>

          <BlockBidTable
            blockBids={blockBids.concat(profileBids)}
            createBid={addNewBid}
            updateBid={editBid}
            deleteBid={removeBlockBid}
            disabledBidButtons={disabledBidButtons}
            bidModalTagSelectionContext={bidModalTagSelectionContext}
          />
        </div>
      )}

      {hasSpotBids && (
        <div className={styles.DayAheadBidEditor_Tables}>
          <Title size="medium" title={t('Spot bid editor')} />
          <div className={styles.DayAheadBidEditor_Tables__right}>
            <Button
              onClick={() => {
                postStat('create-spotbids-from-plan', `click`)
                void copySpotBidsFromPlan()
              }}
              secondary
              marginRight
              icon={'fas fa-copy'}
              disabled={disabledBidButtons || bidListBusy}
            >
              {t('Calculate spot bid')}
            </Button>
            <Button
              onClick={() => {
                setTypeOfBidToCopy('spotbid_without_price', partitionId)
              }}
              secondary
              icon={'fas fa-copy'}
              disabled={disabledBidButtons || bidListBusy}
            >
              {t('Copy spot bids from a chosen date')}
            </Button>
          </div>
          <SpotBidTable
            currentDate={{ startTime: datasetStartTime, endTime: datasetEndTime }}
            spotBids={spotBids}
            createBid={addNewSpotBidsForDate}
            updateBid={editSpotBidsForDate}
            deleteBidForDate={removeSpotBidsForDate}
            retryBidList={() => {
              void bidsQuery.refetch()
            }}
            disabledBidButtons={disabledBidButtons}
            bidListBusy={bidListBusy}
            bidListError={bidsQuery.isError}
          />
        </div>
      )}
    </>
  )
}
